// Thin client for the optional backend. The game is local-first: every method fails soft so
// offline play is never interrupted. Used by the account/sync layer (Phase 4).

import type {
  ApiResult,
  HighscoreEntry,
  SessionResponse,
  UserDataResponse,
} from '@shared/types';

const BASE = '/api';

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(BASE + path, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...init,
    });
    if (res.status === 401) return { __status: 401 } as unknown as T;
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export const api = {
  async session(): Promise<SessionResponse> {
    const r = await jsonFetch<SessionResponse>('/auth/session');
    return r ?? { authenticated: false };
  },

  async checkUsername(username: string): Promise<boolean> {
    const r = await jsonFetch<{ available: boolean }>(
      `/auth/check-username?username=${encodeURIComponent(username)}`,
    );
    return !!r?.available;
  },

  async register(username: string, email: string, password: string): Promise<ApiResult> {
    return (
      (await jsonFetch<ApiResult>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ username, email, password }),
      })) ?? { success: false, message: 'Network error' }
    );
  },

  async login(username: string, password: string): Promise<ApiResult> {
    return (
      (await jsonFetch<ApiResult>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      })) ?? { success: false, message: 'Network error' }
    );
  },

  async logout(): Promise<void> {
    await jsonFetch('/auth/logout', { method: 'POST' });
  },

  /** Returns null when offline / not logged in. */
  async getUserData(): Promise<UserDataResponse | null> {
    const r = await jsonFetch<UserDataResponse & { __status?: number }>('/game/user-data');
    if (!r || r.__status === 401) return null;
    return r;
  },

  async saveData(gameData: string, highScore: number): Promise<boolean> {
    const r = await jsonFetch<ApiResult & { __status?: number }>('/game/save-data', {
      method: 'POST',
      body: JSON.stringify({ gameData, highScore }),
    });
    return !!r && r.__status !== 401 && r.success !== false;
  },

  async highscores(limit = 20): Promise<HighscoreEntry[]> {
    const r = await jsonFetch<{ success: boolean; entries: HighscoreEntry[] }>(
      `/highscores?limit=${limit}`,
    );
    return r?.entries ?? [];
  },

  async changeUsername(newUsername: string): Promise<ApiResult> {
    return (
      (await jsonFetch<ApiResult>('/account/change-username', {
        method: 'POST',
        body: JSON.stringify({ newUsername }),
      })) ?? { success: false, message: 'Network error' }
    );
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResult> {
    return (
      (await jsonFetch<ApiResult>('/account/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      })) ?? { success: false, message: 'Network error' }
    );
  },

  async requestReset(email: string): Promise<ApiResult> {
    return (
      (await jsonFetch<ApiResult>('/account/request-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })) ?? { success: false, message: 'Network error' }
    );
  },

  async deleteAccount(password: string): Promise<ApiResult> {
    return (
      (await jsonFetch<ApiResult>('/account/delete', {
        method: 'POST',
        body: JSON.stringify({ password }),
      })) ?? { success: false, message: 'Network error' }
    );
  },
};
